import {
  Button,
  Card,
  CardContent,
  CardFooter,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  showConfirmationPrompt,
  Alert,
  AlertTitle,
  AlertDescription,
} from '@bpinternal/ui-kit-next'
import { useWorkspaces } from '~/hooks'
import { useDeleteAccount } from '~/hooks/account/useDeleteAccount'
import { useQuery } from '../../../services'
import { AlertTriangle } from 'lucide-react'

type AccountDangerZoneProps = { workspaceId: string }

export const AccountDangerZone = ({ workspaceId }: AccountDangerZoneProps) => {
  const { mutate: deleteAccount } = useDeleteAccount()
  const { data: workspaces } = useWorkspaces()
  const { data: bots } = useQuery('workspaces_/$workspaceId_/bots_', { workspaceId })

  function getDeletionRestrictionMessage(): string | undefined {
    if (workspaces && workspaces.length > 1) {
      return `You must delete all the additional workspaces in your account first in order to delete this account. You have ${workspaces.length - 1} additional workspaces remaining.`
    }
    if (bots?.length && bots.length > 0) {
      return `You must delete all bots before you can delete this account. Currently, you have ${bots.length === 1 ? `1 bot` : `${bots.length} bots`} remaining.`
    }
    return undefined
  }

  const cannotDeleteErrorMessage = getDeletionRestrictionMessage()
  const cannotDelete = cannotDeleteErrorMessage !== undefined

  return (
    <Card>
      <CardContent className="p-6">
        <h3 className="pb-2 text-lg text-red-600">Delete Account</h3>
        <p className="text-neutral-900">
          Deleting a workspace is an irreversible process and will permanently erase all data associated with this
          account, including invoices and all related information.
        </p>
        {cannotDelete && (
          <Alert className="mt-4">
            <AlertTriangle className="h-4 w-4" color="orange" />
            <AlertTitle>Action Required</AlertTitle>
            <AlertDescription>{cannotDeleteErrorMessage}</AlertDescription>
          </Alert>
        )}
      </CardContent>
      <CardFooter className="flex justify-end p-6 pt-0">
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <div>
                <Button
                  variant="destructive"
                  disabled={cannotDelete}
                  onClick={() => {
                    showConfirmationPrompt('Are you sure you want to delete your account ?', {
                      title: 'Delete Account',
                      variant: 'danger',
                    }).then(async () => {
                      deleteAccount()
                    })
                  }}
                >
                  Delete Account
                </Button>
              </div>
            </TooltipTrigger>
            {cannotDelete && (
              <TooltipContent>
                <p>{cannotDeleteErrorMessage}</p>
              </TooltipContent>
            )}
          </Tooltip>
        </TooltipProvider>
      </CardFooter>
    </Card>
  )
}
